import React, { useEffect, useMemo, useState } from "react";
import { Box, CssBaseline, Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  drawerWidth,
  GeneralAdminRoutes,
  mainLayoutStyles,
  showNotification,
} from "../../../../../constants";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import CommonHeader from "../../../../../common/CommonHeader";
import TableSearchBar from "../../../../../common/TableSearchBar";
import CommonTable from "../../../../../common/CommonTable";
import QuickTalesTableRow from "../../../../../common/QuickTalesTableRow";
import { Toast } from "../../../../../common/alert";
import { useAuth } from "../../../../../hooks";
import ImagePreviewModal from "../../../../../common/ImagePreviewModal";
import {
  getSingleQuickTale,
  getSingleUser,
} from "../../../../../modules/OrganizationQuickTales/hooks/QuickTalesHooks";
import QTDetailsImageWithLabelSection from "../../../../../common/QTDetailsImageWithLabelSection";
import { images } from "../../../../../constants";
import { uploadGroupImageToStorage } from "../../../../../modules/OrganizationAlbums/hooks/AlbumsHooks";
import { getGroupById, updateGroup } from "../../hooks/GAGroupsHooks";
import { GroupDetailsDataFieldsSection } from "../../components/components";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Tab from "@mui/material/Tab";

const GAGroupDetailsContainer = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [tabValue, setTabValue] = useState("members");

  const [groupDetails, setGroupDetails] = useState(location?.state?.groupDetails || null);
  const [uploadImage, setUploadImage] = useState(null);
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [membersData, setMembersData] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  const [quickTalesData, setQuickTalesData] = useState([]);
  const [selectedQuickTales, setSelectedQuickTales] = useState([]);

  useEffect(() => {
    if (!location?.state?.groupDetails?.id) {
      navigate(GeneralAdminRoutes.groups, { replace: true });
      return;
    }

    // Preserve tab when navigating back from Add Members / Add QuickTales
    if (location?.state?.activeTab === "members" || location?.state?.activeTab === "quicktales") {
      setTabValue(location.state.activeTab);
    }

    fetchGroupDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      const groupId = location.state.groupDetails.id;
      const snap = await getGroupById(groupId);
      const data = snap?.data();
      if (!data) return;

      const normalized = {
        id: snap.id,
        ...data,
        members: Array.isArray(data.members) ? data.members : [],
        quicktales: Array.isArray(data.quicktales) ? data.quicktales : [],
      };

      // hydrate createdBy -> createdByData for display (like Album details)
      if (normalized?.createdBy) {
        try {
          const u = await getSingleUser(normalized.createdBy);
          normalized.createdByData = u?.data() ? u.data() : null;
        } catch (e) {
          // ignore
        }
      }
      setGroupDetails(normalized);

      // Resolve members
      const memberDocs = await Promise.all(
        normalized.members.map(async (uid) => {
          const u = await getSingleUser(uid);
          return u?.data() ? { id: uid, ...u.data() } : { id: uid };
        })
      );
      setMembersData(memberDocs);

      // Resolve quicktales in order
      const qtDocs = [];
      for (const qtId of normalized.quicktales) {
        const qtSnap = await getSingleQuickTale(qtId);
        if (qtSnap?.data()) {
          const qtData = { id: qtSnap.id, ...qtSnap.data() };

          // hydrate createdBy/narrator -> user data so table shows usernames like other places
          try {
            if (qtData?.createdBy) {
              const u = await getSingleUser(qtData.createdBy);
              qtData.createdByData = u?.data() ? u.data() : null;
            }
          } catch (e) {
            // ignore
          }
          try {
            if (qtData?.narrator) {
              const n = await getSingleUser(qtData.narrator);
              qtData.narratorData = n?.data() ? n.data() : null;
            }
          } catch (e) {
            // ignore
          }

          qtDocs.push(qtData);
        }
      }
      setQuickTalesData(qtDocs);
    } catch (error) {
      console.error("Error fetching group details:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!groupDetails?.id) return null;

  const handleUpdateGroup = async () => {
    try {
      setLoading(true);

      const status = (groupDetails?.status || "").toLowerCase();
      if (status !== "active" && status !== "inactive" && status !== "draft") {
        Toast("error", "Status must be 'active','draft', or 'inactive'.");
        return;
      }

      const patch = {
        name: groupDetails?.name ?? "",
        description: groupDetails?.description ?? "",
        status,
        updatedAt: new Date(),
        updatedBy: currentUser?.userName,
      };

      // Upload new/edited group image if present
      if (uploadImage != null) {
        const photoUrl = await uploadGroupImageToStorage(uploadImage?.raw);
        patch.imageUrls = [photoUrl];
        patch.originalPhotoName = uploadImage?.raw?.name;
      }

      await updateGroup(groupDetails.id, patch);
      showNotification("Group updated successfully.");
    } catch (error) {
      Toast("error", error.message);
    } finally {
      setLoading(false);
      setEditEnabled(false);
      setUploadImage(null);
      await fetchGroupDetails();
    }
  };

  const handleRemoveMembers = async (selectedIds) => {
    try {
      setLoading(true);
      const updated = (groupDetails.members || []).filter((id) => !selectedIds.includes(id));
      await updateGroup(groupDetails.id, {
        members: updated,
        updatedAt: new Date(),
        updatedBy: currentUser?.id,
      });
      setSelectedMembers([]);
      showNotification("Member(s) removed successfully.");
      await fetchGroupDetails();
    } catch (error) {
      Toast("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveQuickTales = async (selectedIds) => {
    try {
      setLoading(true);
      const updated = (groupDetails.quicktales || []).filter((id) => !selectedIds.includes(id));
      await updateGroup(groupDetails.id, {
        quicktales: updated,
        updatedAt: new Date(),
        updatedBy: currentUser?.id,
      });
      setSelectedQuickTales([]);
      showNotification("QuickTale(s) removed successfully.");
      await fetchGroupDetails();
    } catch (error) {
      Toast("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", width: `calc(100% - ${drawerWidth}px)` }}>
      <CssBaseline />
      <DetailsPageHeader
        onBackClick={() => navigate(GeneralAdminRoutes.groups, { replace: true })}
        mainTitle={groupDetails?.name || "Group"}
        bgColor="#fff"
        buttonText={currentUser?.role === "moderator" ? undefined : "Edit"}
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={editEnabled}
        handleSave={handleUpdateGroup}
        loading={loading}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          {/* Group image (QuickTale-like UX: always show + upload/edit/crop in edit mode) */}
          <QTDetailsImageWithLabelSection
            label={"Group Photo"}
            image={
              groupDetails?.imageUrls?.[0]
                ? groupDetails.imageUrls[0]
                : images.QuicktaleLogo
            }
            fileName={groupDetails?.imageFileNames?.[0] || groupDetails?.originalPhotoName}
            handleImagePreviewModalOpen={() => setImagePreviewModal(true)}
            editEnabled={editEnabled}
            details={groupDetails}
            setDetails={setGroupDetails}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            isCreateMode={false}
            autoOpenCropOnUpload={true}
          />

          <GroupDetailsDataFieldsSection
            groupDetails={groupDetails}
            setGroupDetails={setGroupDetails}
            editEnabled={editEnabled}
          />

          <Box sx={{ width: "100%", typography: "body1" }}>
            <TabContext value={tabValue}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList
                  onChange={(e, v) => setTabValue(v)}
                  aria-label="Group tabs"
                >
                  <Tab label="Members" style={{ textTransform: "none" }} value="members" />
                  <Tab label="QuickTales" style={{ textTransform: "none" }} value="quicktales" />
                </TabList>
              </Box>

              <TabPanel value="members" style={{ paddingLeft: 0, paddingRight: 0 }}>
                <CommonHeader
                  mainTitle={"Members"}
                  bgColor="#fff"
                  buttonText={"Add Members"}
                  handleSubmit={() =>
                    navigate(GeneralAdminRoutes.addMembersToGroup, {
                      state: { groupDetails, activeTab: "members" },
                    })
                  }
                />

                <TableSearchBar
                  selected={selectedMembers}
                  setSelected={setSelectedMembers}
                  placeholder={"Search"}
                  handleRemove={handleRemoveMembers}
                  handleSearch={(e) => {
                    const val = e.target.value.toLowerCase();
                    if (!val) {
                      fetchGroupDetails();
                      return;
                    }
                    setMembersData((prev) =>
                      prev.filter((u) =>
                        `${u.firstName || ""} ${u.lastName || ""}`
                          .toLowerCase()
                          .includes(val)
                      )
                    );
                  }}
                />

                <CommonTable
                  tableData={membersData}
                  setTableData={setMembersData}
                  selected={selectedMembers}
                  setSelected={setSelectedMembers}
                  CommonTableRow={DummyUserRow}
                  showPosition={false}
                  tableHeadData={membersHeadCells}
                />
              </TabPanel>

              <TabPanel value="quicktales" style={{ paddingLeft: 0, paddingRight: 0 }}>
                <CommonHeader
                  mainTitle={"QuickTales"}
                  bgColor="#fff"
                  buttonText={"Add QuickTales"}
                  handleSubmit={() =>
                    navigate(GeneralAdminRoutes.addQuickTalesToGroup, {
                      state: { groupDetails, activeTab: "quicktales" },
                    })
                  }
                />
                <TableSearchBar
                  selected={selectedQuickTales}
                  setSelected={setSelectedQuickTales}
                  placeholder={"Search"}
                  handleRemove={handleRemoveQuickTales}
                  handleSearch={(e) => {
                    const val = e.target.value.toLowerCase();
                    if (!val) {
                      fetchGroupDetails();
                      return;
                    }
                    setQuickTalesData((prev) =>
                      prev.filter((qt) => (qt.title || "").toLowerCase().includes(val))
                    );
                  }}
                />

                <CommonTable
                  moduleDetails={groupDetails}
                  module="group"
                  tableData={quickTalesData}
                  setTableData={setQuickTalesData}
                  selected={selectedQuickTales}
                  setSelected={setSelectedQuickTales}
                  CommonTableRow={QuickTalesTableRow}
                  removeRow={handleRemoveQuickTales}
                  showPosition={true}
                  handleSavePosition={async (reorderedIds) => {
                    // CommonTable for non-area modules passes reordered IDs array
                    await updateGroup(groupDetails.id, {
                      quicktales: reorderedIds,
                      updatedAt: new Date(),
                      updatedBy: currentUser?.id,
                    });
                  }}
                />
              </TabPanel>
            </TabContext>
          </Box>
        </Grid>
      </Grid>

      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={() => setImagePreviewModal(false)}
        image={
          uploadImage != null && uploadImage.preview
            ? uploadImage.preview
            : groupDetails?.imageUrls?.[0] || images.QuicktaleLogo
        }
      />
    </Box>
  );
};

export default GAGroupDetailsContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};

const membersHeadCells = [
  { id: "firstName", numeric: false, disablePadding: true, label: "First Name", width: 200 },
  { id: "lastName", numeric: false, disablePadding: true, label: "Last Name", width: 200 },
  { id: "userName", numeric: false, disablePadding: true, label: "Username", width: 220 },
  { id: "id", numeric: false, disablePadding: true, label: "User ID", width: 260 },
];

// Minimal row renderer compatible with CommonTable
const DummyUserRow = ({
  isItemSelected,
  row,
  numberType,
  handleClickRow,
  labelId,
  provided,
  columnWidths,
}) => {
  const { Checkbox, TableCell, TableRow } = require("@mui/material");

  return (
    <TableRow
      ref={provided?.innerRef}
      {...(provided ? provided.draggableProps : {})}
      hover
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.id}
      selected={isItemSelected}
      sx={{
        cursor: "pointer",
        height: 48,
        background: numberType === true ? "#F2F2F2" : "#fff",
      }}
    >
      <TableCell
        padding="checkbox"
        style={{ background: "#fff" }}
        onClick={(event) => handleClickRow(event, row.id)}
      >
        <Checkbox
          color="primary"
          checked={isItemSelected}
          inputProps={{ "aria-labelledby": labelId }}
        />
      </TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{row?.firstName || ""}</TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{row?.lastName || ""}</TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{row?.userName || ""}</TableCell>
      <TableCell style={{ border: "1px solid #BABABA" }}>{row?.id || ""}</TableCell>
    </TableRow>
  );
};
